import { Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { Container, Row } from 'react-grid-system';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import * as yup from 'yup';
import Button from '../../components/Button';
import FlashMessage from '../../components/FlashMessage';
import { Input } from '../../components/FormFields';
import FieldWithLabel from '../../components/FormFields/FieldWithLabel';
import Modal from '../../components/Modal';
import { Header3, P2 } from '../../components/Typography';
import { createErrorMessageSelector } from '../../redux/error/error.selector';
import { createLoadingSelector } from '../../redux/loading/loading.selector';
import {
  UPDATE_ORG_BANK_ACCOUNT_VERIFICATION_NAME_MODAL_STORE_KEY,
  UPDATE_ORG_BANK_ACCOUNT_VERIFICATION_NAME_STORE_KEY,
  updateOrganizationBankAccountVerificationNameRequested,
} from '../../redux/organization-bank-account/organization-bank-account.actions';
import { FLASH_MESSAGE_TIMEOUT, useFlashMessage } from '../FlashMessage';

interface EditVerificationNameModalProps {
  organizationId: string;
  bankAccountId: string;
  verificationName?: string | null;
  onDismiss: () => void;
}

interface EditVerificationNameFormValues {
  verificationName: string;
}

const VALIDATION_SCHEMA = yup.object().shape({
  verificationName: yup.string().trim().required('Required'),
});

const Actions = styled(Row)`
  display: flex;
  justify-content: flex-end;
  column-gap: 8px;
  padding-top: 24px;
`;

const EditVerificationNameModal: React.FC<EditVerificationNameModalProps> = ({
  organizationId,
  bankAccountId,
  verificationName,
  onDismiss,
}) => {
  const dispatch = useDispatch();
  const isSubmitting = useSelector(createLoadingSelector([UPDATE_ORG_BANK_ACCOUNT_VERIFICATION_NAME_STORE_KEY]));
  const requestError = useSelector(createErrorMessageSelector([UPDATE_ORG_BANK_ACCOUNT_VERIFICATION_NAME_STORE_KEY]));
  const [flashMessage, onDismissFlashMessage] = useFlashMessage([UPDATE_ORG_BANK_ACCOUNT_VERIFICATION_NAME_MODAL_STORE_KEY]);
  const wasSubmitting = useRef(false);
  const [isConfirmingClear, setIsConfirmingClear] = useState(false);

  useEffect(() => {
    const finishedSubmitting = wasSubmitting.current && !isSubmitting;
    wasSubmitting.current = isSubmitting;

    if (finishedSubmitting && !requestError) {
      onDismiss();
    }
  }, [isSubmitting, onDismiss, requestError]);

  const handleDismiss = () => {
    onDismissFlashMessage();
    onDismiss();
  };

  const handleClear = () => {
    onDismissFlashMessage();
    dispatch(
      updateOrganizationBankAccountVerificationNameRequested({
        organizationId,
        id: bankAccountId,
        verificationName: null,
      }),
    );
  };

  return (
    <Modal onDismiss={handleDismiss}>
      {flashMessage ? (
        <FlashMessage payload={flashMessage} onDismiss={onDismissFlashMessage} timeRemaining={FLASH_MESSAGE_TIMEOUT} />
      ) : null}
      <Header3 style={{ marginBottom: 6 }}>Edit verification name</Header3>
      <P2>This name is used to verify a bank transaction.</P2>
      <Formik<EditVerificationNameFormValues>
        initialValues={{ verificationName: verificationName || '' }}
        validationSchema={VALIDATION_SCHEMA}
        onSubmit={(values) => {
          onDismissFlashMessage();
          dispatch(
            updateOrganizationBankAccountVerificationNameRequested({
              organizationId,
              id: bankAccountId,
              verificationName: values.verificationName.trim(),
            }),
          );
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, resetForm, touched, values }) => (
          <form onSubmit={handleSubmit}>
            <FieldWithLabel label="Verification name" error={errors.verificationName} touched={touched.verificationName}>
              <Input
                disabled={isConfirmingClear}
                name="verificationName"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="Name used for bank verification"
                value={values.verificationName}
              />
            </FieldWithLabel>
            <Container fluid>
              {isConfirmingClear ? (
                <>
                  <P2 style={{ paddingTop: 24 }}>
                    Are you sure you want to clear the verification name? Bank transactions will no longer be verified against it.
                  </P2>
                  <Actions>
                    <Button buttonType="secondary" noArrow={true} onClick={() => setIsConfirmingClear(false)} type="button">
                      Cancel
                    </Button>
                    <Button buttonType="warning" disabled={isSubmitting} noArrow={true} onClick={handleClear} type="button">
                      Yes, clear verification name
                    </Button>
                  </Actions>
                </>
              ) : (
                <Actions>
                  {verificationName ? (
                    <Button
                      buttonType="warning"
                      disabled={isSubmitting}
                      noArrow={true}
                      onClick={() => {
                        resetForm();
                        setIsConfirmingClear(true);
                      }}
                      type="button"
                    >
                      Clear verification name
                    </Button>
                  ) : null}
                  <Button buttonType="secondary" noArrow={true} onClick={handleDismiss} type="button">
                    Cancel
                  </Button>
                  <Button disabled={isSubmitting} type="submit">
                    Save
                  </Button>
                </Actions>
              )}
            </Container>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default EditVerificationNameModal;
